import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Forward, KeyRound, LogOut, Mail, Moon, Send, Sun } from "lucide-react";
import { toast } from "sonner";

import {
  clientLogout,
  listClientForms,
  listClientRecords,
  loadClientMe,
  requestClientLogin,
  type ClientSharedRecord,
  type ClientSummary,
  listClientSubmissions,
} from "@/lib/client-api";
import { useTheme } from "@/lib/theme";
import { ForwardShareDialog } from "@/components/forward-share-dialog";
import { PortalShell } from "@/components/portal/portal-shell";
import { PortalHeader } from "@/components/portal/portal-header";
import { PortalSubmissionsByForm } from "@/components/portal/portal-submissions-by-form";
import { PortalSection } from "@/components/portal/portal-section";
import { PortalEmptyState } from "@/components/portal/portal-empty-state";
import { PortalFooter } from "@/components/portal/portal-footer";
import { PortalFormCard } from "@/components/portal/portal-form-card";
import {
  iconForRecord,
  portalRecordHref,
} from "@/lib/portal-record";

export const Route = createFileRoute("/$tenantSlug/client/")({
  component: ClientPortal,
});

function ClientPortal() {
  const { tenantSlug } = Route.useParams();
  const qc = useQueryClient();
  const { data: meData, isLoading, isError } = useQuery({
    queryKey: ["client-me", tenantSlug] as const,
    queryFn: () => loadClientMe(tenantSlug),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
        Loading&hellip;
      </div>
    );
  }

  if (isError || !meData?.contact) {
    return <ClientLoginForm tenantSlug={tenantSlug} />;
  }

  return (
    <ClientDashboard
      tenantSlug={tenantSlug}
      contact={meData.contact}
      onLogout={() => qc.invalidateQueries({ queryKey: ["client-me", tenantSlug] })}
    />
  );
}

function ClientLoginForm({ tenantSlug }: { tenantSlug: string }) {
  const [email, setEmail] = useState("");
  const [requested, setRequested] = useState(false);
  const submit = useMutation({
    mutationFn: () => requestClientLogin(tenantSlug, email.trim()),
    onSuccess: () => setRequested(true),
    onError: () => toast.error("Could not send the sign-in link"),
  });

  return (
    <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-8 space-y-4">
        <header className="space-y-1">
          <h1 className="text-xl font-medium inline-flex items-center gap-2">
            <KeyRound className="size-5" /> Sign in
          </h1>
          <p className="text-sm text-slate-500">
            Enter your email address. We&apos;ll send you a one-time sign-in
            link &mdash; no password required.
          </p>
        </header>
        {requested ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700 p-3 space-y-1">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <Mail className="size-4" /> Check your inbox
            </div>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
              If your email is registered, you&apos;ll receive a sign-in link
              within a few minutes. The link expires after 1 hour.
            </p>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submit.isPending || !email.includes("@")}
              className="w-full rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
            >
              {submit.isPending ? "Sending…" : "Send sign-in link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ClientDashboard({
  tenantSlug,
  contact,
  onLogout,
}: {
  tenantSlug: string;
  contact: ClientSummary;
  onLogout: () => void;
}) {
  const { data: shared = { contact: null, records: [], resources: [] }, isLoading: sharedLoading } = useQuery({
    queryKey: ["client-records", tenantSlug] as const,
    queryFn: () => listClientRecords(tenantSlug),
  });
  const { data: forms = [] } = useQuery({
    queryKey: ["client-forms", tenantSlug] as const,
    queryFn: () => listClientForms(tenantSlug),
    staleTime: 30_000,
  });

  const { data: submissions = [], isLoading: submissionsLoading } = useQuery({
    queryKey: ["client-submissions", tenantSlug] as const,
    queryFn: () => listClientSubmissions(tenantSlug),
    staleTime: 30_000,
  });

  const logout = useMutation({
    mutationFn: () => clientLogout(tenantSlug),
    onSuccess: () => onLogout(),
  });
  const { theme, toggle: toggleTheme } = useTheme();
  // Account name flows from the portal service alongside the contact
  // fields. `shared.contact` is the same source both the contact and
  // the account name come from; fall back to `contact` prop when the
  // records query hasn't resolved yet so the header never flashes empty.
  const accountName = shared.contact?.account_name ?? null;

  return (
    <PortalShell>
      <PortalHeader
        title={contact.name || contact.email}
        subtitle={`${accountName ? `${accountName} · ` : ""}${contact.email}`}
        actions={
          <>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="inline-flex items-center justify-center text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          </>
        }
      />

      {forms.length > 0 ? (
        <PortalSection title="Submit" icon={Send}>
          <ul className="grid sm:grid-cols-2 gap-3">
            {forms.map((form) => (
              <PortalFormCard
                key={form.slug}
                tenantSlug={tenantSlug}
                form={form}
                fromPortal="client"
              />
            ))}
          </ul>
        </PortalSection>
      ) : null}

      <PortalSection title="Resources">
        {sharedLoading ? (
          <p className="text-sm text-slate-500">Loading&hellip;</p>
        ) : shared.resources.length === 0 ? (
          <PortalEmptyState message="No public resources yet. Decks, documents, pages, and links the team publishes to every client will land here." />
        ) : (
          <ul className="space-y-2">
            {shared.resources.map((r) => (
              <ClientRecordRow
                key={`${r.entity_slug}:${r.record_id}`}
                tenantSlug={tenantSlug}
                record={r}
              />
            ))}
          </ul>
        )}
      </PortalSection>

      {sharedLoading ? (
        <PortalSection title="Shared with you">
          <p className="text-sm text-slate-500">Loading&hellip;</p>
        </PortalSection>
      ) : shared.records.length === 0 ? (
        <PortalSection title="Shared with you">
          <PortalEmptyState message="Nothing has been shared with you yet. Invoices, statements, and other records the team shares with you will land here." />
        </PortalSection>
      ) : (
        groupByEntity(shared.records).map(([title, records]) => (
          <PortalSection key={title} title={title}>
            <ul className="space-y-2">
              {records.map((r) => (
                <ClientRecordRow
                  key={`${r.entity_slug}:${r.record_id}`}
                  tenantSlug={tenantSlug}
                  record={r}
                />
              ))}
            </ul>
          </PortalSection>
        ))
      )}

      <PortalSubmissionsByForm
        forms={forms}
        submissions={submissions}
        loading={submissionsLoading}
      />

      <PortalFooter />
    </PortalShell>
  );
}

function ClientRecordRow({
  tenantSlug,
  record,
}: {
  tenantSlug: string;
  record: ClientSharedRecord;
}) {
  const [forwardOpen, setForwardOpen] = useState(false);
  const href = portalRecordHref(tenantSlug, record, "client");
  const entityLabel = record.entity_title || record.entity_slug;
  const isAccountGrant = record.grantee_type === "account";
  const title = record.title || `${entityLabel} #${record.record_id}`;
  const Icon = iconForRecord(record);
  return (
    <li className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-start gap-3">
      <div className="shrink-0 size-9 rounded bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-500">
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
          <span className="truncate">{title}</span>
          {record.amount_display ? (
            <span className="text-slate-500 dark:text-slate-400 font-normal">
              · {record.amount_display}
            </span>
          ) : null}
          {record.status ? (
            <StatusPill label={record.status} tier={record.status_tier ?? "default"} />
          ) : null}
        </div>
        {record.description ? (
          <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
            {record.description}
          </p>
        ) : record.subtitle ? (
          <p className="text-xs text-slate-500 line-clamp-2">{record.subtitle}</p>
        ) : null}
        {record.due_date ? (
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Due {new Date(record.due_date).toLocaleDateString()}
          </p>
        ) : null}
        {record.message ? (
          <p className="text-xs text-slate-500 italic">&ldquo;{record.message}&rdquo;</p>
        ) : null}
        <div className="text-[11px] text-slate-400">
          {entityLabel} shared
          {record.granted_at ? ` ${new Date(record.granted_at).toLocaleDateString()}` : ""}
          {record.expires_at ? ` — expires ${new Date(record.expires_at).toLocaleDateString()}` : ""}
          {isAccountGrant ? " — shared with your company" : null}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setForwardOpen(true)}
          className="inline-flex items-center gap-1 text-xs rounded border border-slate-300 dark:border-slate-700 px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <Forward className="size-3" /> Forward
        </button>
        <a
          href={href}
          className="inline-flex items-center gap-1 text-xs rounded border border-slate-300 dark:border-slate-700 px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Open <ExternalLink className="size-3" />
        </a>
      </div>
      <ForwardShareDialog
        tenantSlug={tenantSlug}
        sourceGrantId={record.record_access_id}
        recordTitle={title}
        open={forwardOpen}
        onOpenChange={setForwardOpen}
      />
    </li>
  );
}

/**
 * Coloured status pill for the shared-with-you row. `tier` is a
 * semantic label sourced from the invoice_statuses row (paid /
 * overdue / partial / draft / authorised / sent) — the server side
 * maps the tenant's `is_paid` / `is_overdue` etc. flags to one of
 * these so a rename of the status label doesn't change the colour.
 * `default` is the fallback for statuses that don't set any flag.
 */
function StatusPill({
  label,
  tier,
}: {
  label: string;
  tier: NonNullable<ClientSharedRecord["status_tier"]>;
}) {
  const styles: Record<typeof tier, string> = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
    overdue: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800",
    partial: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
    draft: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700",
    authorised: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800",
    sent: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800",
    default: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${styles[tier]}`}
    >
      {label}
    </span>
  );
}

/*
 * One section per aggregate, titled by the entity itself.
 *
 * Nothing here knows which entities exist. The server sends each row's
 * `entity_title` from the entity's own row, so a newly sharable
 * aggregate gets its own section with the right name and a renamed one
 * follows automatically. Sections are ordered by title so the list is
 * stable between loads.
 */
function groupByEntity(
  records: ClientSharedRecord[],
): Array<[string, ClientSharedRecord[]]> {
  const groups = new Map<string, ClientSharedRecord[]>();
  for (const r of records) {
    const title = r.entity_title || r.entity_slug;
    const bucket = groups.get(title);
    if (bucket) bucket.push(r);
    else groups.set(title, [r]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}
